import { Box, Card, Divider, Typography } from '@mui/material'

import { US_DATE_TIME_WITH_MONTH_NAME } from '@/api/constants'
import CardHeader from '@/common/components/Card/CardHeader'
import { theme } from '@/theme/theme'
import { Campaign, CampaignStatus } from '@/types/campaigns'
import dayjs from 'dayjs'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import CanceledCampaignTimeFrame from './CanceledCampaignTimeFrame'
import ErrorCampaignTimeFrame from './ErrorCampaignTimeFrame'
import Flag from './Flag'
import Rocket from './Rocket'
import Timer from './Timer'
import { useCampaign } from '@/api/campaigns/get'

type CampaignTimeFrameProps = {
  campaignId: string
}

const CampaignTimeFrame: FC<CampaignTimeFrameProps> = ({ campaignId }) => {
  const { t } = useTranslation()

  const { data: campaignData, isPending: isPendingCampaign } = useCampaign(campaignId)

  const startDate = dayjs(campaignData?.schedule?.start)
  const endDate = dayjs(campaignData?.schedule?.end)
  const updateDate = dayjs(campaignData?.updated_at)
  const started = dayjs().isAfter(startDate)
  const ended = dayjs().isAfter(endDate)

  if (isPendingCampaign || !campaignData) return null // TODO Add Loading animation
  return (
    <Card>
      <CardHeader title={t('campaignDashboard.timeFrame.title')}></CardHeader>
      {campaignData.status === CampaignStatus.canceled ? (
        <CanceledCampaignTimeFrame startDate={startDate} endDate={endDate} updateDate={updateDate} />
      ) : campaignData.status === CampaignStatus.error ? (
        <ErrorCampaignTimeFrame />
      ) : (
        <Box display="flex" flexDirection="column" gap={1} justifyContent="center" paddingTop={3}>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography>
                {t(started ? 'campaignDashboard.timeFrame.started' : 'campaignDashboard.timeFrame.scheduled')}
              </Typography>
              <Typography fontWeight={theme.typography.fontWeightSemiBold} textTransform="uppercase">
                {startDate.format(US_DATE_TIME_WITH_MONTH_NAME)}
              </Typography>
            </Box>
            <Box display="flex" alignItems="flex-end" flexDirection="column">
              <Typography>
                {t(ended ? 'campaignDashboard.timeFrame.ended' : 'campaignDashboard.timeFrame.endsAt')}
              </Typography>
              <Typography fontWeight={theme.typography.fontWeightSemiBold} textTransform="uppercase" textAlign={'end'}>
                {endDate.format(US_DATE_TIME_WITH_MONTH_NAME)}
              </Typography>
            </Box>
          </Box>
          <Flag />
          <Divider sx={{ width: '100%', margin: theme.spacing(6, 0, 1) }} />
          <Rocket position={ended ? 'right' : started ? 'center' : 'left'} />
          <Timer endDate={endDate} />
        </Box>
      )}
    </Card>
  )
}

export default CampaignTimeFrame
